package fr.manyverse.app.infrastructure.realtime

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.NetworkError
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.realtime.Realtime
import io.github.jan.supabase.realtime.RealtimeChannel
import io.github.jan.supabase.realtime.realtime
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.channels.ProducerScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.transformLatest
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

private val SUBSCRIBE_STALL_TIMEOUT = 30.seconds
private const val MAX_ERROR_REJOINS = 2
private val STABLE_SUBSCRIPTION_DELAY = 30.seconds
private val CHANNEL_REMOVAL_TIMEOUT = 5.seconds

/**
 * Abonne [channel] pour le compte d'un flux (`channelFlow`) et le supervise jusqu'à sa fermeture.
 *
 * supabase-kt ne remonte jamais l'échec d'un channel, qui prend deux formes :
 * - join refusé (quota de channels, accès au channel privé refusé…) : après quelques tentatives le channel reste en
 *   `UNSUBSCRIBED` et `subscribe(blockUntilSubscribed = true)` reste suspendu indéfiniment. On logge
 *   une erreur dès que le channel reste non abonné [SUBSCRIBE_STALL_TIMEOUT] alors que la
 *   websocket est connectée. Hors ligne, l'attente est normale et n'est pas signalée.
 * - join accepté puis erreur du serveur (filtre ou table invalide pour postgres_changes) : le
 *   channel est `SUBSCRIBED` quand l'erreur arrive, supabase-kt le quitte pour le rejoindre, ce
 *   qui remet à zéro son compteur de tentatives : la boucle est sans fin. Pire, le départ ne
 *   reçoit jamais de réponse et c'est le timeout de heartbeat qui finit par couper la websocket,
 *   avec tous les autres channels, environ toutes les 40 s. On logge une erreur dès que le
 *   channel est quitté ainsi [MAX_ERROR_REJOINS] fois sans rester abonné
 *   [STABLE_SUBSCRIPTION_DELAY] entre-temps (une erreur isolée peut venir d'un jeton expiré).
 *
 * À la fermeture du flow, le channel est retiré du client Realtime : sinon il reste abonné sur la
 * websocket et est rejoint à chaque reconnexion.
 *
 * Comme `subscribe(blockUntilSubscribed = true)`, suspend jusqu'à ce que le channel soit abonné.
 * Les `postgresChangeFlow` doivent donc être déclarés avant l'appel ; les écoutes Broadcast peuvent
 * l'être après.
 *
 * Après une coupure, supabase-kt réabonne le channel mais ne rejoue pas les messages manqués : voir
 * [resubscriptions] pour recharger l'état complet.
 */
internal suspend fun ProducerScope<*>.subscribeSupervised(
    supabaseClient: SupabaseClient,
    channel: RealtimeChannel,
    tag: String
) {
    launch {
        errorRejoins(channel.status, MAX_ERROR_REJOINS, STABLE_SUBSCRIPTION_DELAY).collect { rejoins ->
            AppLogger.error(
                tag,
                "Channel Realtime ${channel.topic} en erreur côté serveur, rejoint $rejoins fois",
                NetworkError("Realtime channel rejected by server")
            )
        }
    }
    launch {
        try {
            subscriptionStalls(
                isConnected = supabaseClient.realtime.status.map { it == Realtime.Status.CONNECTED },
                isSubscribed = channel.status.map { it == RealtimeChannel.Status.SUBSCRIBED },
                timeout = SUBSCRIBE_STALL_TIMEOUT
            ).collect {
                AppLogger.error(
                    tag,
                    "Channel Realtime ${channel.topic} toujours non abonné après " +
                        "$SUBSCRIBE_STALL_TIMEOUT (statut ${channel.status.value})",
                    NetworkError("Realtime channel subscription stalled")
                )
            }
        } finally {
            withContext(NonCancellable) { removeChannel(supabaseClient, channel, tag) }
        }
    }
    channel.subscribe(blockUntilSubscribed = true)
}

private suspend fun removeChannel(supabaseClient: SupabaseClient, channel: RealtimeChannel, tag: String) {
    try {
        // Le départ du channel passe par la websocket : on ne bloque pas la fermeture du flow
        // si elle ne répond plus
        withTimeoutOrNull(CHANNEL_REMOVAL_TIMEOUT) {
            supabaseClient.realtime.removeChannel(channel)
        } ?: AppLogger.warn(tag, "Retrait du channel Realtime ${channel.topic} expiré")
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        AppLogger.warn(tag, "Retrait du channel Realtime ${channel.topic} impossible : ${e::class.simpleName}")
    }
}

/**
 * Émet une fois chaque fois que le channel reste non abonné pendant [timeout] alors que la
 * connexion est établie. Le compte à rebours repart de zéro dès que l'une des deux conditions
 * change.
 */
@OptIn(ExperimentalCoroutinesApi::class)
internal fun subscriptionStalls(
    isConnected: Flow<Boolean>,
    isSubscribed: Flow<Boolean>,
    timeout: Duration
): Flow<Unit> =
    combine(isConnected, isSubscribed) { connected, subscribed -> connected && !subscribed }
        .distinctUntilChanged()
        .transformLatest { stalled ->
            if (stalled) {
                delay(timeout)
                emit(Unit)
            }
        }

/**
 * Émet chaque fois que le channel redevient abonné après l'avoir déjà été : coupure réseau,
 * reconnexion de la websocket ou rejoin après une erreur du serveur.
 */
internal fun resubscriptions(status: Flow<RealtimeChannel.Status>): Flow<Unit> = flow {
    var subscribedBefore = false
    status.distinctUntilChanged().collect { current ->
        if (current == RealtimeChannel.Status.SUBSCRIBED) {
            if (subscribedBefore) emit(Unit)
            subscribedBefore = true
        }
    }
}

/**
 * Émet chaque fois que la connexion revient après avoir été perdue, une fois [awaitResubscribed]
 * terminé. Une perte avant la toute première connexion n'est pas une coupure ; une nouvelle perte
 * pendant l'attente annule l'émission en cours.
 */
@OptIn(ExperimentalCoroutinesApi::class)
internal fun reconnections(
    isConnected: Flow<Boolean>,
    awaitResubscribed: suspend () -> Unit
): Flow<Unit> = channelFlow {
    var connectedBefore = false
    var lost = false
    isConnected.distinctUntilChanged().collectLatest { connected ->
        if (!connected) {
            if (connectedBefore) lost = true
            return@collectLatest
        }
        connectedBefore = true
        if (lost) {
            awaitResubscribed()
            lost = false
            send(Unit)
        }
    }
}

/**
 * Émet le nombre de rejoins quand le channel est quitté pour la [maxRejoins]-ième fois à la suite
 * d'une erreur, sans être resté abonné [stableAfter] entre-temps. Émet une seule fois par série :
 * le compteur ne repart de zéro qu'une fois l'abonnement stable.
 *
 * Un passage direct de `SUBSCRIBED` à `UNSUBSCRIBING` n'est provoqué que par une erreur du
 * serveur (supabase-kt quitte puis rejoint le channel) ou une fermeture imposée par le serveur.
 * Une coupure réseau passe directement à `UNSUBSCRIBED`, et notre propre retrait du channel
 * n'arrive qu'une fois cette surveillance annulée.
 */
internal fun errorRejoins(
    status: Flow<RealtimeChannel.Status>,
    maxRejoins: Int,
    stableAfter: Duration
): Flow<Int> = channelFlow {
    var rejoins = 0
    var previous: RealtimeChannel.Status? = null
    status.distinctUntilChanged().collectLatest { current ->
        val leftAfterError = previous == RealtimeChannel.Status.SUBSCRIBED &&
            current == RealtimeChannel.Status.UNSUBSCRIBING
        previous = current
        if (leftAfterError) {
            rejoins++
            if (rejoins == maxRejoins) send(rejoins)
        } else if (current == RealtimeChannel.Status.SUBSCRIBED) {
            // Annulé si le statut change avant la fin du délai
            delay(stableAfter)
            rejoins = 0
        }
    }
}
